import {Injectable, OnDestroy} from '@angular/core';
import {I18NextPipe} from 'angular-i18next';
import {Sentry} from 'app/core/error-handler/sentry.service';
import {dateOffset, getDateString, isValidateDateValue, listDefaultRanges} from 'app/core/helpers/date';
import {cloneDeep, isEqualDeep} from 'app/core/helpers/object';
import {AuthService} from 'app/core/services/auth.service';
import {DemoService} from 'app/core/services/demo.service';
import {SessionStorageService} from 'app/core/services/session-storage.service';
import {UserpilotService} from 'app/core/services/userpilot.service';
import {
    ReportsLayoutIconName,
    ReportsLayoutType,
} from 'app/features/analytics/features/reports/components/reports-page/reports-layout.enum';
import {
    accountOptions,
    AppSplit,
    appSplitOptions,
    Dimension,
    KnownMeasure,
    TimeBucket,
    timeDimensionOptions,
} from 'app/features/analytics/features/reports/dimension';
import {DimensionsAndMeasuresService} from 'app/features/analytics/service/dimensions-and-measures.service';
import {FilterAutoOpenService} from 'app/features/analytics/service/filter-auto-open.service';
import {BoundedFilters, getBoundedFilters} from 'app/features/analytics/service/filter-options.helpers';
import {
    AppGroup,
    ColumnPinned,
    DEFAULT_TOP_LIMIT,
    dimensionAvailableFilterModes,
    FilterMode,
    FilterValue,
    Group,
    MeasureToAdd,
    NetworkAccountGroup,
    ReportFilters,
    SelectedMeasure,
    TimeFilterValue,
    TimeGroup,
} from 'app/features/analytics/service/filter-value.interface';
import {network_hid_map} from 'app/features/analytics/service/filter-value.service.map.networks';
import {StatsHelperService} from 'app/features/analytics/service/stats-helper.service';
import {measureColumnKey, ReportsMode} from 'app/features/analytics/shared/components/analytics-table/analytics-table.helpers';
import {ColorSettings} from 'app/features/analytics/shared/components/analytics-table/components/form-coloring-cells/form-coloring-cells.component';
import {fillRateTypeLabel, getTimeBucket, isTimeDimension, requestTypeLabel} from 'app/features/analytics/shared/utils';
import moment from 'moment-mini';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {DateDimension, FilterDimension, HavingFilterOperator, Dimension as GraphQLDimension} from '../../../../globalTypes';
import {omitDeep} from '@apollo/client/utilities';

const hidRegEx = /^\d+$/;

function isValidHid(v) {
    return hidRegEx.test(v);
}

@Injectable({
    providedIn: 'root',
})
export class FilterValueService implements OnDestroy {
    public readonly value$ = new BehaviorSubject<ReportFilters>(this.initialValue);
    public valueWithUser$: Observable<ReportFilters>;

    public boundedFilters$: Observable<BoundedFilters>;
    public hasUserOrAppFilter$: Observable<boolean>;
    public hasAppFilter$: Observable<boolean>;

    public disabledMetrics: Record<string, boolean> = {};
    public disabledDimensions: Record<Dimension, boolean> = <any>{};
    private latestAvailableRevenuePredictionVersions: string[];

    activeFilterId: string;

    sub = Subscription.EMPTY;

    protected lastIsDemoValue = false;

    reset() {
        this.value = this.initialValue;
    }

    public get initialValue(): ReportFilters {
        return {
            ...this.getInitialValue(),
            order: {},
            isDemo: this.lastIsDemoValue,
            view: ReportsLayoutType.TABLE,
        };
    }

    protected getInitialValue() {
        return {
            filter: [],
            groupBy: [],
            measures: [],
            sortBy: [],
            campaignProps: [],
            isDemo: this.lastIsDemoValue,
            columnPinned: [],
            order: {} as Record<string, number>,
            colorFormatting: {} as Record<KnownMeasure, ColorSettings>,
            forecastVersion: '',
            isMultiMediation: false,
        };
    }

    public setLatestAvailableRevenuePredictionVersions(latestAvailableRevenuePredictionVersions) {
        this.latestAvailableRevenuePredictionVersions = latestAvailableRevenuePredictionVersions;
    }

    public get value(): ReportFilters {
        return this.value$.getValue();
    }

    public set value(v) {
        this.value$.next(v);
    }

    beforeEmit(v: ReportFilters): ReportFilters {
        const reqLabel = requestTypeLabel(v);
        const req = v.measures.find(m => m.id === KnownMeasure.Requests);
        if (req && req.label !== this.i18Next.transform(reqLabel)) {
            req.label = this.i18Next.transform(reqLabel);
        }

        const fillLabel = fillRateTypeLabel(v);
        const fill = v.measures.find(m => m.id === KnownMeasure.Fillrate);
        if (fill && fill.label !== this.i18Next.transform(fillLabel)) {
            fill.label = this.i18Next.transform(fillLabel);
        }

        return v;
    }

    public emit(v?: ReportFilters) {
        v = v ? v : {...this.value};
        this.helper.trace.next();
        console.log(`ReportFilters traceId ${this.helper.trace.traceId}`);
        v = this.beforeEmit(v);
        this.value$.next(v);
    }

    public emitPartialChange(v: Partial<ReportFilters> | Record<string, any>) {
        return this.emit({...this.value, ...v});
    }

    public getFiltersForSave(): Partial<ReportFilters> {
        return omitDeep(this.value, 'savedReportId');
    }

    constructor(
        protected dms: DimensionsAndMeasuresService,
        protected auth: AuthService,
        protected autoOpen: FilterAutoOpenService,
        protected sessionStorage: SessionStorageService,
        protected i18Next: I18NextPipe,
        protected userpilotService: UserpilotService,
        protected demo: DemoService,
        protected helper: StatsHelperService,
        protected sentry: Sentry
    ) {
        this.afterCreate();
        this.sub = this.userpilotService.getIsTourRunning().subscribe(isDemo => {
            this.lastIsDemoValue = isDemo || this.demo.isDemoHost;
            this.emit({...this.value, isDemo});
        });
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    afterCreate() {
        this.boundedFilters$ = this.value$.pipe(
            map(getBoundedFilters),
            distinctUntilChanged((a, b) => isEqualDeep(a, b))
        );

        this.hasAppFilter$ = this.value$.pipe(
            map(v => v.filter.some(f => f.id === Dimension.App && f.value && f.value.length)),
            distinctUntilChanged()
        );

        this.hasUserOrAppFilter$ = this.value$.pipe(
            map(
                v =>
                    !this.auth.canViewMultiUserStats ||
                    v.filter.some(f => f.id === Dimension.User && f.value && f.value.length) ||
                    v.filter.some(f => f.id === Dimension.App && f.value && f.value.length)
            )
        );

        this.valueWithUser$ = this.value$.pipe(
            map(filterValue => {
                let filter = filterValue.filter.map(f => {
                    if (isTimeDimension(f.id)) {
                        return f['relativeDate']
                            ? {
                                  id: f.id,
                                  relativeDate: f['relativeDate'],
                              }
                            : {
                                  id: f.id,
                                  from: f['from'],
                                  to: f['to'],
                              };
                    }

                    return f;
                });

                if (this.auth.canViewMultiMediationStats) {
                    filterValue.isMultiMediation = true;
                }

                if (this.auth.canViewMultiUserStats) {
                    return {
                        ...filterValue,
                        filter,
                    };
                }

                // const filters;
                const userFilterIndex = filter.findIndex(f => f.id === Dimension.User);
                const defaultUserFilter = {
                    id: FilterDimension.USER,
                    value: [this.auth.currentUserId],
                };
                if (userFilterIndex === -1) {
                    // add to start
                    filter.unshift(defaultUserFilter);
                } else {
                    // replace
                    filter[userFilterIndex] = defaultUserFilter;
                }

                const groupBy = filterValue.groupBy.filter(g => g.id !== Dimension.User);

                return {
                    ...filterValue,
                    groupBy,
                    filter,
                };
            })
        );
    }

    setDefaultUserIdFilter(oldValue?: string) {
        let v: string[] = [];
        if (!this.auth.canViewMultiUserStats) {
            console.debug(`User can see only it's own stats`);
            v = [this.auth.currentUserId];
        } else if (oldValue) {
            v = [oldValue];
        }

        const position = this.value.filter.some(f => f.id === Dimension.User) ? undefined : 0;

        this.addFilter(
            {
                id: Dimension.User,
                value: v,
            },
            false,
            position
        );
    }

    private disableMetrics() {
        this.disabledMetrics = <any>{};
        // no dimension - nothing to disable
        if (this.value.filter.length === 0 && this.value.groupBy.length === 0) {
            return;
        }

        const disable = (list: {id: string}[], disabledMetrics: Record<string, boolean>) => {
            let metricsMap: Record<string, number> = <any>{};
            Object.keys(this.dms.metricsMap).forEach(m => (metricsMap[m] = 0));
            list.forEach(filterOrGroup => {
                this.dms.dimensionMap[filterOrGroup.id]?.supportedMeasures.forEach(d => metricsMap[d]++);
            });
            // find dimensions supported by each selected measure
            Object.keys(metricsMap).forEach((d: string) => {
                disabledMetrics[d] = disabledMetrics[d] || metricsMap[d] !== list.length;
            });
        };

        disable(
            this.value.filter.filter(({id}) => id !== Dimension.User),
            this.disabledMetrics
        );
        disable(this.value.groupBy, this.disabledMetrics);
    }

    private disableDimensions() {
        // find all supported dimensions map for selected values

        const disabledDimensions = (this.disabledDimensions = <any>{});
        if (this.value.measures.length === 0) {
            return;
        }

        const dimensionMap: Record<Dimension, number> = <any>{};
        Object.keys(this.dms.dimensionMap).forEach(d => (dimensionMap[d] = 0));
        const uniqMetrics = this.value.measures.reduce((acc, m) => {
            // to make uniq list
            acc.set(m.id, m);
            return acc;
        }, new Map());

        uniqMetrics.forEach(m => this.dms.metricsMap[m?.id]?.supportedDimensions.forEach(d => dimensionMap[d]++));

        // find dimensions supported by each selected measure
        Object.keys(dimensionMap).forEach((d: Dimension) => {
            disabledDimensions[d] = dimensionMap[d] !== uniqMetrics.size;
        });
    }

    addMeasure(toAddMeasure: MeasureToAdd | string | KnownMeasure, emit = false, positionMeasure?: number) {
        const selectedMeasure = this.dms.toSelectedMeasure(toAddMeasure);

        if (!selectedMeasure) {
            return true;
        }

        if (this.disabledMetrics[selectedMeasure.id]) {
            return true;
        }

        if (!this.value.measures.some(measure => measure.id === selectedMeasure.id && measure.day === selectedMeasure.day)) {
            const filters = cloneDeep(this.value);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            positionMeasure >= 0 ? filters.measures.splice(positionMeasure, 0, selectedMeasure) : filters.measures.push(selectedMeasure);
            this.value = filters;

            this.disableDimensions();
        }

        if (emit) {
            this.emit();
        }
        return true;
    }

    removeMeasure(toDelete: MeasureToAdd) {
        const elPost = this.value.measures.findIndex(measure => measure.id === toDelete.id && measure.day === toDelete.day);
        if (elPost !== -1) {
            const filters = cloneDeep(this.value);

            if (filters.order) {
                delete filters.order[measureColumnKey(filters.measures[elPost])];
            }

            if (filters.colorFormatting) {
                delete filters.colorFormatting[measureColumnKey(filters.measures[elPost])];
            }
            if (filters.chartWithMetrics) {
                delete filters.chartWithMetrics[measureColumnKey(filters.measures[elPost])];
            }
            const updateTopBy = filters.topByMetric && measureColumnKey(filters.topByMetric) === measureColumnKey(filters.measures[elPost]);

            if (filters.columnPinned !== undefined && filters.view === ReportsLayoutType.TABLE) {
                let idxPinMeasure = filters.columnPinned.findIndex(pin => pin.field === filters.measures[elPost].id);
                filters.columnPinned.splice(idxPinMeasure, 1);
            }

            filters.measures.splice(elPost, 1);
            if (updateTopBy && filters.measures.length > 0) {
                this.setSopByMetric(filters, filters.measures[0]);
            }
            this.value = filters;

            this.disableDimensions();
        }
        this.emit();
        return true;
    }

    removeMeasuresAll() {
        let filters = cloneDeep(this.value);

        filters.measures.forEach(v => {
            delete filters.order[measureColumnKey(v)];
        });

        this.value = {
            ...filters,
            colorFormatting: <Record<KnownMeasure, ColorSettings>>{},
            columnPinned: this.removeAllPinnedColumns(filters, 'measures'),
            measures: [],
        };
        this.disableDimensions();

        this.emit();
        return true;
    }

    removeAllPinnedColumns(filters: ReportFilters, filterBy: string): ColumnPinned[] {
        if (!filters.columnPinned) {
            return;
        }

        return filters.columnPinned
            .map(pin => {
                if (!filters[filterBy].some(group => group.id === pin.field)) {
                    return pin;
                }
            })
            .filter(item => item !== undefined);
    }

    addFilter(toAddFilter: Record<string, any> | string | Dimension, showMenuOnInit?: boolean, positionFilter?: number) {
        const fil = this.buildFilter(toAddFilter);
        if (!this.dms.isKnownDimension(fil.id)) {
            return true;
        }

        if (this.disabledDimensions[fil.id]) {
            return true;
        }

        if (showMenuOnInit) {
            this.autoOpen.markToOpen(fil.id);
        }

        this.patchDeprecatedFilter(fil);
        this.validateAndFixHids(fil);
        if (this.dms.deprecatedDimensionsMap[fil.id]) {
            this.sentry.sendMessage(`Unable to patch hid filter ${fil.id}`);
        }

        if (!this.value.filter.some(f => f.id === fil.id && fil.mode === f.mode)) {
            const filters = cloneDeep(this.value);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            positionFilter >= 0 ? filters.filter.splice(positionFilter, 0, fil) : filters.filter.push(fil);

            this.value = filters;
            this.disableMetrics();
        } else {
            const index = this.value.filter.findIndex(f => f.id === fil.id);
            this.value.filter[index] = cloneDeep(this.value.filter[index]);
        }

        this.emit();
        return true;
    }

    validateAndFixHids(fil: FilterValue) {
        if (
            (
                [
                    Dimension.attribution_network_hid,
                    Dimension.attribution_campaign_hid,
                    Dimension.attribution_creative_hid,
                    Dimension.attribution_ad_set_hid,
                ] as string[]
            ).includes(fil.id) &&
            Array.isArray(fil.value) &&
            fil.value.some(v => !isValidHid(v))
        ) {
            fil.value = fil.value.filter(isValidHid);
        }
    }

    patchDeprecatedFilter(fil: FilterValue) {
        if (fil.id === Dimension.attribution_network) {
            this.sentry.leaveBreadCrumbs({category: 'hid', message: `${fil.id} replaced`});
            fil.id = Dimension.attribution_network_hid;
            if ((fil.value && fil.mode === FilterMode.Include) || fil.mode === FilterMode.Exclude) {
                fil.value = (Array.isArray(fil.value) ? fil.value : [fil.value])
                    .map(v => network_hid_map.get(v.toLowerCase()))
                    .filter(v => v !== undefined && v !== null);
            }
        }

        if (this.dms.deprecatedDimensionsMap[fil.id] && (fil.mode === FilterMode.Contains || fil.mode === FilterMode.DoesNotContains)) {
            this.sentry.leaveBreadCrumbs({category: 'hid', message: `${fil.id} replaced`});
            fil.id = this.dms.deprecatedDimensionsMap[fil.id];
        }

        if (
            this.dms.deprecatedDimensionsMap[fil.id] &&
            (((fil.mode === FilterMode.Include || fil.mode === FilterMode.Exclude) && !fil.value.length) ||
                fil.mode === FilterMode.Contains ||
                fil.mode === FilterMode.DoesNotContains)
        ) {
            this.sentry.leaveBreadCrumbs({category: 'hid', message: `${fil.id} replaced`});
            fil.id = this.dms.deprecatedDimensionsMap[fil.id];
        }
    }

    addFilterValue(dimension: Dimension, value: Partial<TimeFilterValue> | string, mode: FilterMode = FilterMode.Include) {
        // this.updateFilterByName();
        // eslint-disable-next-line max-len
        const filterIndex = this.value.filter.findIndex(
            filterItem => filterItem.id === dimension && (filterItem.mode ? filterItem.mode === mode : true)
        );
        const filter = this.buildFilter(
            typeof value === 'object'
                ? {
                      id: dimension,
                      mode,
                      ...value,
                  }
                : {
                      id: dimension,
                      mode,
                      value: [value],
                  }
        );

        if (filterIndex === -1) {
            return this.emit({...this.value, filter: [...this.value.filter, filter]});
        } else {
            const filters = [...this.value.filter];
            filters[filterIndex] = cloneDeep(filters[filterIndex]);

            if (filters[filterIndex].value && typeof value === 'string') {
                (filters[filterIndex].value as string[]).push(value);
            } else {
                filters[filterIndex]['from'] = filter['from'];
                filters[filterIndex]['to'] = filter['to'];
            }

            return this.emit({...this.value, filter: filters});
        }
    }

    updateFilter(): void {
        this.disableMetrics();
        this.emit();
    }

    updateMeasures(): void {
        this.disableDimensions();
        this.emit();
    }

    updateMode(mode: ReportsMode, emit = true): void {
        if (!this.auth.getCurrentUserLastValue().permissions.canViewAdminPanel && (mode === 4 || mode === 5)) {
            mode = ReportsMode.SummaryReport;
        }

        this.value.mode = Object.values(ReportsMode).includes(Number(mode)) ? Number(mode) : ReportsMode.SummaryReport;

        if (emit) {
            this.emit();
        }
        this.sessionStorage.setItem('anonymizationMode', String(mode));
    }

    updateReportsView(view: ReportsLayoutType, emit = true): void {
        this.value.view = ReportsLayoutIconName[view] ? view : ReportsLayoutType.TABLE;
        if (this.value.view === ReportsLayoutType.BAR_CHART || this.value.view === ReportsLayoutType.LINE_CHART) {
            if (!this.value.topLimit) {
                this.value.topLimit = DEFAULT_TOP_LIMIT;
            }
            if (this.value.topByMetric !== null && this.value.measures.length > 0) {
                this.setSopByMetric(this.value, this.value.topByMetric);
            }
        }
        if (emit) {
            this.emit();
        }
    }

    setSopByMetric(val: ReportFilters, m: SelectedMeasure): ReportFilters {
        val.topByMetric = {
            id: m.id,
            day: m.day,
            label: m.label,
        };
        return val;
    }

    removeFilter(id: string) {
        const elPost = this.value.filter.findIndex(f => f.id === id);
        if (elPost !== -1) {
            const filters = cloneDeep(this.value);
            filters.filter.splice(elPost, 1);
            this.value = filters;
            this.disableMetrics();
        }
        this.emit();
        return true;
    }

    removeFiltersAll() {
        this.value = {
            ...cloneDeep(this.value),
            filter: [],
        };
        this.disableMetrics();

        this.emit();
        return true;
    }

    validateDateValue(from: string, to: string): {from: string; to: string} {
        if (!isValidateDateValue(from, to)) {
            return this.defaultTimeDimensionValue();
        }
        if (from > to) {
            return {
                from: to,
                to: from,
            };
        }
        return {
            from,
            to,
        };
    }

    defaultDateValue() {
        return {
            from: getDateString(dateOffset(new Date(), {days: -7})),
            to: getDateString(new Date()),
        };
    }

    defaultTimeDimensionValue() {
        const v = this.value.filter.find(f => isTimeDimension(f.id)) as TimeFilterValue;
        return v ? {from: v.from, to: v.to} : this.defaultDateValue();
    }

    buildFilter(filter: Record<string, any> | string): FilterValue | TimeFilterValue {
        if (typeof filter === 'string') {
            filter = {id: filter};
        }
        if (isTimeDimension(filter.id)) {
            if (filter.relativeDate) {
                const relativeDate = filter.relativeDate;
                const range = listDefaultRanges[relativeDate];

                if (!range) {
                    return <TimeFilterValue>{
                        id: filter.id,
                        ...this.defaultTimeDimensionValue(),
                    };
                }

                const rangeFrom = moment(range[0]).format('YYYY-MM-DD');
                const rangeTo = moment(range[1]).format('YYYY-MM-DD');

                return <TimeFilterValue>{
                    id: filter.id,
                    ...this.validateDateValue(rangeFrom, rangeTo),
                    relativeDate,
                };
            } else {
                return <TimeFilterValue>{
                    id: filter.id,
                    ...this.validateDateValue(filter.from, filter.to),
                };
            }
        }
        if (filter.id === FilterDimension.DAY_SINCE_INSTALL) {
            return <FilterValue>{
                id: filter.id,
                value: '',
                mode: FilterMode.Contains,
                search: filter.search ? String(filter.search) : '',
            };
        }

        // validate modes

        const availableModes = dimensionAvailableFilterModes(filter.id);
        const mode = availableModes.includes(filter.mode) ? filter.mode : FilterMode.Include;

        return <FilterValue>{
            id: filter.id,
            value: Array.isArray(filter.value) ? filter.value : [],
            mode,
            search: (mode === FilterMode.Contains || mode === FilterMode.DoesNotContains) && filter.search ? String(filter.search) : '',
        };
    }

    protected internalAddGroup(
        input: Record<string, any> | string,
        groupBy: (Group | TimeGroup)[],
        positionGroup?: number
    ): (Group | TimeGroup)[] {
        if (!input) {
            return groupBy;
        }

        const group = this.buildGroup(input);
        if (!this.dms.isKnownDimension(group.id)) {
            return groupBy;
        }
        if (this.disabledDimensions[group.id]) {
            return groupBy;
        }
        if (this.dms.deprecatedDimensionsMap[group.id]) {
            group.id = this.dms.deprecatedDimensionsMap[group.id];
        }
        if (groupBy.some(f => f.id === group.id)) {
            return groupBy;
        }

        groupBy = cloneDeep(groupBy);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        positionGroup >= 0 ? groupBy.splice(positionGroup, 0, group) : groupBy.push(group);
        return groupBy;
    }

    addGroup(input: Record<string, any> | string, positionGroup?: number, emit?: boolean) {
        const groupBy = this.internalAddGroup(input, this.value.groupBy, positionGroup);
        if (this.value.groupBy === groupBy) {
            return;
        }

        const group = this.buildGroup(input);
        if (group.id === Dimension.ATTRIBUTION_SOURCE_APP) {
            let installs = this.value.measures.find(m => m.id === KnownMeasure.Installs);
            if (!installs) {
                installs = this.dms.toSelectedMeasure(KnownMeasure.Installs);
            }
            if (!installs.having) {
                // @ts-ignore
                installs.having = {
                    [HavingFilterOperator.MORE]: 1,
                };
            }
            this.updateMetric(installs, installs);
        }

        this.disableMetrics();

        if (emit || emit === undefined) {
            this.emit({...this.value, groupBy});
        } else {
            this.value.groupBy = groupBy;
        }
        return true;
    }

    removeGroup(id: string) {
        const elPost = this.value.groupBy.findIndex(g => g.id === id);
        if (elPost !== -1) {
            const filters = cloneDeep(this.value);

            if (filters.columnPinned !== undefined && filters.view === ReportsLayoutType.TABLE) {
                let idxPinMeasure = filters.columnPinned.findIndex(pin => pin.field === filters.groupBy[elPost].id);
                filters.columnPinned.splice(idxPinMeasure, 1);
            }
            delete filters.order[id];

            filters.groupBy.splice(elPost, 1);
            this.value = filters;
            this.disableMetrics();
        }
        this.emit();
        return true;
    }

    removeGroupAll() {
        let filters = cloneDeep(this.value);

        filters.groupBy.forEach(v => {
            delete filters.order[v.id];
        });

        this.value = {
            ...filters,
            columnPinned: this.removeAllPinnedColumns(filters, 'groupBy'),
            groupBy: [],
        };
        this.disableMetrics();

        this.emit();
        return true;
    }

    buildGroup(group: Record<string, any> | string): Group {
        if (typeof group === 'string') {
            group = {id: group};
        }
        if (
            group.id === Dimension.Time ||
            group.id === Dimension.InstallDate ||
            group.id === Dimension.app_created_date ||
            group.id === Dimension.user_created_date
        ) {
            return <TimeGroup>{
                id: group.id,
                timeBucket: timeDimensionOptions.some(opt => opt.value === (<TimeGroup>group).timeBucket)
                    ? group.timeBucket
                    : TimeBucket.DAY,
            };
        }
        if (group.id === Dimension.App) {
            return <AppGroup>{
                id: group.id,
                display: appSplitOptions.some(opt => opt.value === (<AppGroup>group).display) ? group.display : AppSplit.Name,
            };
        }
        if (group.id === GraphQLDimension.NETWORK_ACCOUNT) {
            return <NetworkAccountGroup>{
                id: group.id,
                display: accountOptions.some(opt => opt.value === (<AppGroup>group).display) ? group.display : AppSplit.Name,
            };
        }
        return <Group>{
            id: group.id,
        };
    }

    updateFilterByName(name: string, filterValue: FilterValue): void {
        const filters = cloneDeep(this.value);
        const filterIndex = filters.filter.findIndex(filterItem => filterItem.id === name);
        if (filterValue.value && this.dms.deprecatedDimensionsMap[filterValue.id] && filterValue.value.length === 0) {
            this.patchDeprecatedFilter(filterValue);
        }
        this.validateAndFixHids(filterValue);
        if (filterIndex !== -1) {
            filters.filter[filterIndex] = filterValue;
        } else {
            filters.filter.push(filterValue);
            if (!([Dimension.Time, Dimension.User] as string[]).includes(filterValue.id)) {
                this.autoOpen.markToOpen(filterValue.id);
            }
        }

        this.value = filters;
        this.emit();
    }

    updateGroupByName(name: string, groupBy: Group | TimeGroup): void {
        const filters = cloneDeep(this.value);
        const groupByIndex = filters.groupBy.findIndex(groupByItem => groupByItem.id === name);

        if (groupByIndex !== -1) {
            filters.groupBy[groupByIndex] = groupBy;
        } else {
            filters.groupBy.push(groupBy);
        }

        this.value = filters;
        this.emit();
    }

    updateMetric(m: SelectedMeasure, nextValue: SelectedMeasure) {
        if (m.id !== nextValue.id || m.day !== m.day) {
            throw new Error(`Inconsistent new metrics value ${JSON.stringify(m)} ${JSON.stringify(nextValue)}`);
        }
        const normalized = this.dms.toSelectedMeasure(nextValue);
        const index = this.value.measures.findIndex(measure => measure.id === m.id && measure.day === m.day);
        if (index === -1) {
            this.addMeasure(normalized);
        } else {
            const measures = [...this.value.measures];
            measures[index] = cloneDeep(normalized);
            this.value = {
                ...this.value,
                measures,
            };
        }
    }

    setFilterProperty(field: string, value: any): void {
        const filters = cloneDeep(this.value);
        filters[field] = value;
        this.value = filters;
        this.emit();
    }

    setFilterProperties(properties): void {
        this.value = {...cloneDeep(this.value), ...properties};
        this.emit();
    }

    disabledWhy(id: string): string {
        if (this.dms.metricsMap[id] && this.disabledMetrics[id]) {
            const reportsFilters = this.value;
            const filtersAndGroups = [...new Set([reportsFilters.filter, reportsFilters.groupBy].flat().map(v => v.id))];
            return this.i18Next.transform('analytics.reports.disabledMetricsTooltip', {
                dimensions: filtersAndGroups
                    .filter(dm => !this.dms.metricsMap[id].supportedDimensions.includes(dm as any))
                    .map(dm => this.dms.dimensionMap[dm]?.label)
                    .join(', '),
            });
        }

        if (this.dms.dimensionMap[id] && this.disabledDimensions[id]) {
            const reportsFilters = this.value;
            const filtersAndGroups = [...new Set([reportsFilters.measures].flat().map(v => v.id))];
            return this.i18Next.transform('analytics.reports.disabledDimensionTooltip', {
                metrics: filtersAndGroups
                    .filter(metric => !this.dms.dimensionMap[id].supportedMeasures.includes(metric as any))
                    .map(dm => this.dms.metricsMap[dm]?.label)
                    .join(', '),
            });
        }
        return '';
    }

    onGetNewFilterFromQuery(reportFilter: ReportFilters, defaultDateDimension = DateDimension.DATE) {
        this.reset();
        if (!reportFilter.filter.some(f => isTimeDimension(f.id))) {
            this.addFilter({id: defaultDateDimension});
        }
        if (reportFilter.userId) {
            this.setDefaultUserIdFilter(reportFilter.userId);
        }

        this.value.campaignProps = reportFilter.campaignProps || [];
        reportFilter.measures.forEach(measure => this.addMeasure(measure));
        reportFilter.groupBy.forEach(g => this.addGroup(g));
        reportFilter.filter.forEach(f => this.addFilter(f));
        this.value.sortBy = reportFilter.sortBy;
        this.value.columnPinned = reportFilter.columnPinned || [];
        this.value.colorFormatting = reportFilter.colorFormatting || ({} as any);
        this.value.chartWithMetrics = reportFilter.chartWithMetrics || ({} as any);
        this.value.order = reportFilter.order || {};
        this.value.topFilter = reportFilter.topFilter;
        this.value.topLimit = reportFilter.topLimit || DEFAULT_TOP_LIMIT;
        this.value.topByMetric = reportFilter.topByMetric || null;

        this.updateReportsView(reportFilter.view, false);

        this.updateMode(reportFilter.mode, false);
        if (this.latestAvailableRevenuePredictionVersions) {
            this.value.forecastVersion = this.latestAvailableRevenuePredictionVersions.includes(reportFilter.forecastVersion)
                ? reportFilter.forecastVersion
                : '';
        } else {
            this.value.forecastVersion = reportFilter.forecastVersion;
        }

        this.value.isMultiMediation = reportFilter.isMultiMediation;
        this.value.savedReport = reportFilter.savedReport;
        this.value.savedReportId = reportFilter.savedReportId;

        this.value.pagination = reportFilter.pagination;
        this.emit();
    }

    useOneDateDimension(currentFilters: ReportFilters, dmToPreserve) {
        const dmToRemove = Object.values(DateDimension).find(d => d !== dmToPreserve);

        // if both filters - remove one
        if (
            currentFilters.filter.some(f => f.id === DateDimension.DATE) &&
            currentFilters.filter.some(f => f.id === DateDimension.INSTALL_DATE)
        ) {
            this.removeFilter(dmToRemove);
        } else if (currentFilters.filter.some(f => f.id === dmToRemove)) {
            // otherwise - replace with preserving value
            const index = currentFilters.filter.findIndex(g => g.id === dmToRemove);
            const clone = {...currentFilters.filter[index], id: dmToPreserve};
            this.addFilter(clone, false, index);
            this.removeFilter(dmToRemove);
        }

        // if both split - remove one
        if (
            currentFilters.groupBy.some(f => f.id === DateDimension.DATE) &&
            currentFilters.groupBy.some(f => f.id === DateDimension.INSTALL_DATE)
        ) {
            this.removeGroup(dmToRemove);
        } else if (currentFilters.groupBy.some(f => f.id === dmToRemove)) {
            // otherwise - replace with preserving timeBucket
            const index = currentFilters.groupBy.findIndex(g => g.id === dmToRemove);
            const timeBucket = getTimeBucket(currentFilters);
            this.removeGroup(dmToRemove);
            this.addGroup(
                {
                    id: dmToPreserve,
                    timeBucket,
                },
                index
            );

            this.removeGroup(dmToRemove);
        }
    }
}
